import React from 'react';
import CgEntityHoverText from './CgEntityHoverText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox } from '@fortawesome/free-solid-svg-icons';
import { useFetchCnGoodsCbam } from 'modules/Common/hooks/useFetchCnGoodsCbam';
import { useCnGoodsContext } from '../contexts/CnGoodsContext';
import { useHistory } from 'react-router-dom';
import CgButton from 'modules/Common/ui/CgButton';
import CgBenchmarkBadge from 'modules/Common/ui/CgBenchmarkBadge';
import useEmissionsNavigationStore from 'modules/Emissions/stores/useEmissionsNavigationStore';
import { useAllCountries } from 'modules/Common/utils/Utility';
import formatCnCode from 'modules/Common/utils/formatCnCode';
import formatReportingPeriod from '../utils/formatReportingPeriod';
import { useTranslation } from 'react-i18next';
import CgEmissionFactorDisclaimer from './CgEmissionFactorDisclaimer';

// variant:
//   'chip'  (default) — bordered white pill with box icon + teal CN code;
//           the legacy look used inside dense/legacy tables.
//   'plain' — table-native text: teal with a dotted underline. Same hover
//           card + actions, only the trigger changes.
export default function CgCnGoodHoverText({
  cnGood,
  cnCode,
  cnGoodId,
  position = 'auto',
  supplier = null,
  showAddEmissionsAction = false,
  // Optional override for the "Add Emissions Data" action. Receives the
  // resolved context ({ supplierId, supplierName, cnGoodId, cnGoodCode,
  // cnGoodName, installationId }) so hosts can open an in-place dialog
  // instead of the legacy /emissions redirect (which stays the fallback).
  onAddEmissionsData = null,
  useAutonomousData = false,
  emissionFactor = null,
  defaultEmissionFactor = null,
  showBenchmark = false,
  benchmarkData = null,
  defaultBenchmarkData = null,
  variant = 'chip',
  // Item 16C (fixpack 2026-07-23): 'hover' everywhere except surfaces that
  // need a tap-friendly trigger (an iframe'd public preview, an embeddable
  // widget) — forwarded to CgEntityHoverText/CgPopover, which already
  // support it.
  trigger = 'hover',
  // Item 16C: additive-only override so a cramped host (the ~8-11px
  // PortalWidget) can shrink the trigger's type without touching the
  // default look every other platform caller gets. Appended AFTER the
  // variant's own classes — use Tailwind's `!` important prefix (already
  // used elsewhere in this codebase, e.g. SharePortalPage's `!mb-0`) for a
  // reliable override, since two same-specificity utility classes are
  // otherwise ordered by source scan, not by className position.
  triggerClassName = '',
}) {
  const { t } = useTranslation('common');
  const { data: cnGoods, isLoading: cnGoodsLoading } = useFetchCnGoodsCbam();
  const context = useCnGoodsContext();
  const history = useHistory();
  const countries = useAllCountries();
  const { navigateToEmissions } = useEmissionsNavigationStore();

  // Determine display logic based on granularity and available data
  const granularity = emissionFactor?.granularity;
  const actualEmissionFactorIsDefault = ['Global', 'Country'].includes(granularity);

  let showActualSection = false;
  let showDefaultSection = false;
  let actualFactorsToShow = null;
  let actualBenchmarkToShow = null;
  let actualEmissionFactorData = null;
  let defaultFactorsToShow = null;
  let defaultBenchmarkToShow = null;
  let defaultEmissionFactorData = null;
  let markupPercentage = null;

  if (actualEmissionFactorIsDefault) {
    // Using default granularity - show as default section
    if (defaultEmissionFactor) {
      // Separate default data provided
      showDefaultSection = true;
      defaultFactorsToShow = defaultEmissionFactor.emissionFactors;
      defaultBenchmarkToShow = defaultBenchmarkData;
      defaultEmissionFactorData = defaultEmissionFactor;
    } else {
      // No separate default, show emissionFactor data in default section
      showDefaultSection = true;
      defaultFactorsToShow = emissionFactor?.emissionFactors;
      defaultBenchmarkToShow = benchmarkData;
      defaultEmissionFactorData = emissionFactor;
    }
  } else {
    // Using actual data - show as actual section
    showActualSection = true;
    actualFactorsToShow = emissionFactor?.emissionFactors;
    actualBenchmarkToShow = benchmarkData;
    actualEmissionFactorData = emissionFactor;

    // Also show default section if default data is provided
    if (defaultEmissionFactor) {
      showDefaultSection = true;
      defaultFactorsToShow = defaultEmissionFactor.emissionFactors;
      defaultBenchmarkToShow = defaultBenchmarkData;
      defaultEmissionFactorData = defaultEmissionFactor;
    }
  }

  // Get country name for default factor
  const defaultCountry =
    defaultEmissionFactorData?.countryId && countries.length > 0
      ? countries?.find((country) => country._id == defaultEmissionFactorData.countryId)?.name
      : null;

  // Helper function to find CN good with flexible matching
  // Handles format differences like "7208.10" vs "7208.10.00"
  const findCnGood = (goods, code) => {
    if (!goods || !code) return null;
    return goods.find((good) => good._id === formatCnCode(code));
  };

  // Resolve CN Good data based on the mode and available props
  const resolvedCnGood = React.useMemo(() => {
    let baseCnGood = null;

    // If cnGood object is provided directly, use it (backwards compatibility)
    if (cnGood) {
      baseCnGood = cnGood;
    }
    // If autonomous mode is enabled, use CBAM goods data
    else if (useAutonomousData && cnGoods) {
      if (cnGoodId) {
        // Find by ID with flexible matching
        baseCnGood = findCnGood(cnGoods, cnGoodId);
      } else if (cnCode) {
        // Find by CN code with flexible matching
        baseCnGood = findCnGood(cnGoods, cnCode);
      }
    }
    // Fallback to original CBAM data lookup (backwards compatibility)
    else if (cnCode && cnGoods) {
      baseCnGood = findCnGood(cnGoods, cnCode);
    }

    if (!baseCnGood) {
      return null;
    }

    // Merge context data with the base CN good
    const contextKey = baseCnGood.cnCode || cnCode;
    const contextData = context[contextKey] || {};

    return {
      ...baseCnGood,
      ...contextData,
      // Preserve original data if no context override
      _id: baseCnGood._id,
      cnCode: baseCnGood.cnCode,
      name: baseCnGood.name,
    };
  }, [cnGood, cnGoodId, cnCode, useAutonomousData, cnGoods, context]);
  // resolvedCnGood is null when the code is absent from the catalog — the
  // markup lookup must not crash the whole page over one unknown CN code.
  if (defaultEmissionFactorData && resolvedCnGood?.cnCode) {
    let emissionsEndYear = new Date(defaultEmissionFactorData.lastRecordDate).getFullYear();
    const markupYearMapping = {
      2026: 10,
      2027: 20,
    };
    if (resolvedCnGood.cnCode.startsWith('31')) {
      markupPercentage = 1;
    } else {
      markupPercentage = markupYearMapping[emissionsEndYear] ?? 30;
    }
  }
  const handleAddEmissionsData = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (supplier && resolvedCnGood) {
      const context = {
        supplierId: supplier.supplierId || supplier._id,
        supplierName:
          supplier.supplierName ||
          supplier.name ||
          t('common:chase.unknownSupplier', 'Unknown supplier'),
        cnGoodId: resolvedCnGood._id || resolvedCnGood.cnCode,
        cnGoodCode: resolvedCnGood.cnCode || cnCode,
        cnGoodName: resolvedCnGood.name || resolvedCnGood.cnCode || cnCode,
        installationId: resolvedCnGood.installationId || cnGood?.installationId || '',
      };

      // Preferred: hand the context to the host's in-place dialog.
      if (onAddEmissionsData) {
        onAddEmissionsData(context);
        return;
      }

      // Fallback: navigate to the emissions page with a pre-filled modal
      navigateToEmissions({ ...context, sourceContext: 'Supplier Chase' });
      history.push('/emissions');
    }
  };

  const triggerElement =
    variant === 'plain' ? (
      <span
        className={`cursor-pointer text-sm font-medium text-primaryColor underline decoration-dotted decoration-primaryColor/40 underline-offset-2 hover:text-primaryColor/80 hover:decoration-primaryColor/70 transition-colors max-w-[140px] truncate inline-block align-middle ${triggerClassName}`.trim()}
      >
        {formatCnCode(resolvedCnGood?.cnCode || cnCode) ||
          t('common:entityHoverText.unknownCnCode', 'Unknown CN code')}
      </span>
    ) : (
      <span
        className={`cursor-pointer text-sm text-primaryColor transition-colors shadow-sm bg-white border border-gray-200 rounded-md px-2 py-1 max-w-[120px] truncate inline-flex items-center align-middle ${triggerClassName}`.trim()}
      >
        <FontAwesomeIcon
          icon={faBox}
          className="mr-1.5 text-primaryColor"
          size="sm"
        />
        {formatCnCode(resolvedCnGood?.cnCode || cnCode) ||
          t('common:entityHoverText.unknownCnCode', 'Unknown CN code')}
      </span>
    );

  const popoverContent = (
    <>
      <div>
        <p className="text-sm text-gray-800 font-medium mb-1">
          {t('entity.goodInfo', 'Good Information')}
        </p>
        <div className="pl-2">
          <p className="text-xs">
            <span className="font-medium text-gray-800">{t('entity.cnCodeLabel', 'CN Code:')}</span>{' '}
            <span className="text-xs text-gray-600">
              {formatCnCode(resolvedCnGood?.cnCode || cnCode) || '-'}
            </span>
          </p>
          <p className="text-xs">
            <span className="font-medium text-gray-800">{t('entity.nameLabel', 'Name:')}</span>{' '}
            <span className="text-xs text-gray-600">{resolvedCnGood?.name || '-'}</span>
          </p>
          <p className="text-xs">
            <span className="font-medium text-gray-800">{t('entity.typeLabel', 'Type:')}</span>{' '}
            <span className="text-xs text-gray-600">{resolvedCnGood?.goodType || '-'}</span>
          </p>
        </div>
      </div>

      {/* <div>
        <p className="text-sm text-gray-800 font-medium mb-1">
          {t('entity.productionDetails', 'Production Details')}
        </p>
        <div className="pl-2">
          <p className="text-xs">
            <span className="font-medium text-gray-800">{t('entity.emissionUnitLabel', 'Emission Unit:')}</span>{" "}
            <span className="text-xs text-gray-600">
              {cnGood?.emissionsUnit || "-"}
            </span>
          </p>
          <p className="text-xs">
            <span className="font-medium text-gray-800">
              {t('entity.defaultProdMethodLabel', 'Default Prod. Method:')}
            </span>{" "}
            <span className="text-xs text-gray-600">
              {cnGood?.defaultProductionMethod || "-"}
            </span>
          </p>
        </div>
      </div> */}

      {showActualSection && actualFactorsToShow && (
        <div className="border-t border-gray-200 pt-2 mt-2">
          <p className="text-sm text-gray-800 font-medium mb-1">
            {t('entity.actualEmissionFactor', 'Actual Emission Factor')}
          </p>
          <div className="pl-2 space-y-1">
            <p className="text-xs">
              <span className="font-medium text-gray-800">
                {t('entity.directLabel', 'Direct:')}
              </span>{' '}
              <span className="text-xs text-gray-600">
                {actualFactorsToShow.direct != null ? `${actualFactorsToShow.direct} tCO₂e/t` : '-'}
              </span>
            </p>
            <p className="text-xs">
              <span className="font-medium text-gray-800">
                {t('entity.indirectLabel', 'Indirect:')}
              </span>{' '}
              <span className="text-xs text-gray-600">
                {actualFactorsToShow.indirect != null
                  ? `${actualFactorsToShow.indirect} tCO₂e/t`
                  : '-'}
              </span>
            </p>
            <p className="text-xs">
              <span className="font-medium text-gray-800">{t('entity.totalLabel', 'Total:')}</span>{' '}
              <span className="text-xs text-primaryColor font-semibold">
                {actualFactorsToShow.total != null ? `${actualFactorsToShow.total} tCO₂e/t` : '-'}
              </span>
            </p>
            {actualBenchmarkToShow && (
              <p className="text-xs">
                <span className="font-medium text-gray-800">
                  {t('entity.benchmarkLabel', 'Benchmark:')}
                </span>{' '}
                <span className="text-xs text-primaryColor font-semibold">
                  {actualBenchmarkToShow.cbamBenchmark || actualBenchmarkToShow || '-'} tCO₂/t
                </span>
                {actualBenchmarkToShow.benchmarkSource && (
                  <>
                    {' '}
                    <CgBenchmarkBadge
                      benchmarkSource={actualBenchmarkToShow.benchmarkSource}
                      size="sm"
                    />
                  </>
                )}
              </p>
            )}
            <p className="text-xs">
              <span className="font-medium text-gray-800">
                {t('entity.granularityLabel', 'Granularity:')}
              </span>{' '}
              <span className="text-xs text-gray-600 bg-green-50 px-1.5 py-0.5 rounded border border-green-200">
                {actualEmissionFactorData?.granularity || '-'}
              </span>
              {actualEmissionFactorData?.date && actualEmissionFactorData?.lastRecordDate && (
                <span className="ml-1.5 text-xs text-gray-600">
                  (
                  {formatReportingPeriod(
                    actualEmissionFactorData.date,
                    actualEmissionFactorData.lastRecordDate,
                    actualEmissionFactorData.granularity,
                  )}
                  )
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {showDefaultSection && defaultFactorsToShow && (
        <div className="border-t border-gray-200 pt-2 mt-2">
          <p className="text-sm text-gray-800 font-medium mb-1">
            {t('entity.defaultEmissionFactor', 'Default Emission Factor')}
            <span className="ml-1.5 text-xs font-normal text-gray-600">
              (
              {markupPercentage
                ? t('common:entityHoverText.markupIncluded', {
                    percent: markupPercentage,
                    defaultValue: '{{percent}}% Markup Included',
                  })
                : t('common:entityHoverText.markupNotIncluded', 'Markup Not Included')}
              )
            </span>
          </p>
          <div className="pl-2 space-y-1">
            <p className="text-xs">
              <span className="font-medium text-gray-800">
                {t('entity.directLabel', 'Direct:')}
              </span>{' '}
              <span className="text-xs text-gray-600">
                {defaultFactorsToShow.direct != null
                  ? `${defaultFactorsToShow.direct} tCO₂e/t`
                  : '-'}
              </span>
            </p>
            <p className="text-xs">
              <span className="font-medium text-gray-800">
                {t('entity.indirectLabel', 'Indirect:')}
              </span>{' '}
              <span className="text-xs text-gray-600">
                {defaultFactorsToShow.indirect != null
                  ? `${defaultFactorsToShow.indirect} tCO₂e/t`
                  : '-'}
              </span>
            </p>
            <p className="text-xs">
              <span className="font-medium text-gray-800">{t('entity.totalLabel', 'Total:')}</span>{' '}
              <span className="text-xs text-orange-600 font-semibold">
                {defaultFactorsToShow.total != null ? `${defaultFactorsToShow.total} tCO₂e/t` : '-'}
              </span>
            </p>
            {defaultBenchmarkToShow && (
              <p className="text-xs">
                <span className="font-medium text-gray-800">
                  {t('entity.benchmarkLabel', 'Benchmark:')}
                </span>{' '}
                <span className="text-xs text-orange-600 font-semibold">
                  {defaultBenchmarkToShow.cbamBenchmark || defaultBenchmarkToShow || '-'} tCO₂/t
                </span>
                {defaultBenchmarkToShow.benchmarkSource && (
                  <>
                    {' '}
                    <CgBenchmarkBadge
                      benchmarkSource={defaultBenchmarkToShow.benchmarkSource}
                      size="sm"
                    />
                  </>
                )}
              </p>
            )}
            <p className="text-xs">
              <span className="font-medium text-gray-800">
                {t('entity.granularityLabel', 'Granularity:')}
              </span>{' '}
              <span className="text-xs text-gray-600 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-200">
                {defaultCountry || defaultEmissionFactorData?.granularity || '-'}
              </span>
            </p>
            {defaultEmissionFactorData?.disclaimerRequired && (
              <CgEmissionFactorDisclaimer className="mt-1.5" />
            )}
          </div>
        </div>
      )}

      {showAddEmissionsAction && supplier && (
        <div className="border-t border-gray-200 pt-3">
          <p className="text-xs text-gray-800 font-medium mb-3">
            {t('common:table.actions', 'Actions')}
          </p>
          <div className="flex items-center">
            <CgButton
              variant="primary"
              icon="faPlus"
              label={t('entity.addEmissionsData', 'Add Emissions Data')}
              onClick={handleAddEmissionsData}
              size="sm"
              outlined
            />
          </div>
        </div>
      )}
    </>
  );

  return (
    <CgEntityHoverText
      triggerElement={triggerElement}
      popoverContent={popoverContent}
      popoverTitle={t('common:entityHoverText.cnCodeTitle', {
        code: resolvedCnGood?.cnCode || cnCode,
        defaultValue: 'CN Code: {{code}}',
      })}
      position={position}
      trigger={trigger}
      maxWidth="max-w-[350px]"
    />
  );
}
